import { useCallback, useState } from 'react';
import {
  ParticipationSource,
  Session,
  SessionLink,
  StudyType,
  useIncentiveText,
  useSetSessionLinkAsDefault,
  useShortUrl,
} from '@app/api';
import { PriceEdition, SharableLink } from 'components';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { MenuItem, Chip, DotsMenu, IconChip, useToast } from '@app/core';
import { participationSourceColors } from '../../Sessions/Details/components/ParticipationSourceChips';
import { useTranslation } from 'react-i18next';
import { SessionLinkPriceChips } from './SessionLinkPriceChips';
import classNames from 'classnames';
import { EditRedirectionLinks } from './EditRedirectionLinks';
import { EditSessionLink } from './EditSessionLink';
import { SessionLinkIncentiveEdition } from './SessionLinkIncentiveEdition';

interface Props {
  sessionLink: SessionLink;
  session: Session;
}

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  clickable: {
    cursor: 'pointer',
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase,
    '&:hover': {
      backgroundColor: '#00000011',
    },
  },
  notClickable: {
    padding: theme.marginBase,
  },
  id: {
    flex: 1,
    maxWidth: 100,
    ...theme.fonts.label,
  },
  defaultLink: {
    color: theme.colors.greenAccessible,
  },
  source: {
    flex: 1,
    maxWidth: 120,
  },
  prices: {
    flex: 1,
  },
  incentive: {
    ...theme.fonts.caption,
    flex: 1,
  },
  noRedirectionIcon: {
    opacity: 0.7,
  },
  link: {
    width: theme.marginBase * 30,
  },
  editionContent: {
    padding: theme.marginBase * 4,
    maxHeight: 1000,
    transition: 'max-height .3s',
  },
  editionContentClosed: {
    padding: 0,
    maxHeight: 0,
  },
  bilendiIcon: {
    color: '#d91682',
    fontSize: theme.iconSize.small,
  },
  tandemzIcon: {
    color: theme.colors.purple,
    fontSize: theme.iconSize.regular,
  },
  twoCol: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    gap: theme.marginBase * 3,
  },
  col: {
    flex: 1,
    minWidth: '35%',
  },
}));

enum EditionTab {
  REDIRECTION_LINK = 'REDIRECTION_LINK',
  SESSION_LINK = 'SESSION_LINK',
  INCENTIVE = 'INCENTIVE',
  PRICE = 'PRICE',
  ALL_LINKS = 'ALL_LINKS',
}

export const SessionLinkListItem = ({ sessionLink, session }: Props) => {
  const classes = useStyles();
  const shortUrl = useShortUrl();
  const { t } = useTranslation();
  const toast = useToast();

  const studyLink = `${shortUrl}/s/${sessionLink.id}`;
  const presentationLink =
    session.studyType === StudyType.UNMODERATED
      ? `${shortUrl}/spm-quant/${sessionLink.id}`
      : `${shortUrl}/spm/${sessionLink.id}`;
  const bilendiParams = `?tid=%SPM_PANELIST_ID%&panel=%INTERNAL_PANEL%`;

  const incentiveText = useIncentiveText(
    sessionLink.id,
    session,
    session.locale,
  );

  const { mutateAsync: setAsDefaultAsync } = useSetSessionLinkAsDefault(
    sessionLink.id,
  );

  const [openedEdition, setEditionTab] = useState<EditionTab | undefined>(
    undefined,
  );
  const openRedirectionLinks = useCallback(
    () => setEditionTab(EditionTab.REDIRECTION_LINK),
    [setEditionTab],
  );
  const openSessionLink = useCallback(
    () => setEditionTab(EditionTab.SESSION_LINK),
    [setEditionTab],
  );
  const openPriceEdition = useCallback(
    () => setEditionTab(EditionTab.PRICE),
    [setEditionTab],
  );
  const openIncentiveEdition = useCallback(
    () => setEditionTab(EditionTab.INCENTIVE),
    [setEditionTab],
  );
  const openAllLinks = useCallback(
    () => setEditionTab(EditionTab.ALL_LINKS),
    [setEditionTab],
  );
  const closeEdition = useCallback(
    () => setEditionTab(undefined),
    [setEditionTab],
  );
  const setAsDefault = useCallback(async () => {
    try {
      await setAsDefaultAsync({});
      toast.saved();
    } catch (e) {
      toast.exception();
    }
  }, [setAsDefaultAsync]);

  return (
    <div className={classes.container}>
      <div className={classes.row}>
        <div
          className={classes.clickable}
          onClick={
            openedEdition === EditionTab.SESSION_LINK
              ? closeEdition
              : openSessionLink
          }
        >
          <IconChip
            color={sessionLink.recruitmentByTandemz ? 'red' : 'inkSecondary'}
            icon={
              sessionLink.recruitmentByTandemz
                ? 'custom-icon-font-tandemz'
                : 'ri-link-m'
            }
          />
        </div>

        <div
          className={classNames(classes.id, classes.notClickable, {
            [classes.defaultLink]: sessionLink.defaultLink,
          })}
        >
          {sessionLink.id}
        </div>
        <div
          className={classNames(classes.source, classes.clickable)}
          onClick={
            openedEdition === EditionTab.SESSION_LINK
              ? closeEdition
              : openSessionLink
          }
        >
          <Chip color={participationSourceColors[sessionLink.defaultSource]}>
            {t(
              `sessions.details.monitoring.sources.${sessionLink.defaultSource}`,
            )}
          </Chip>
        </div>
        <div
          className={classNames(classes.prices, classes.clickable)}
          onClick={
            openedEdition === EditionTab.PRICE ? closeEdition : openPriceEdition
          }
        >
          <SessionLinkPriceChips
            session={session}
            sessionLinkId={sessionLink.id}
          />
        </div>
        <div
          className={classNames(classes.incentive, classes.clickable)}
          onClick={
            openedEdition === EditionTab.INCENTIVE
              ? closeEdition
              : openIncentiveEdition
          }
        >
          {incentiveText}
        </div>
        <div
          className={classes.clickable}
          onClick={
            openedEdition === EditionTab.REDIRECTION_LINK
              ? closeEdition
              : openRedirectionLinks
          }
        >
          <IconChip
            color={sessionLink.redirectionLinksId ? 'blue' : 'inkSecondary'}
            icon="ri-arrow-go-back-line"
            className={classNames({
              [classes.noRedirectionIcon]: !sessionLink.redirectionLinksId,
            })}
          />
        </div>
        <div className={classes.notClickable}>
          {!sessionLink.recruitmentByTandemz ? (
            <SharableLink link={studyLink} className={classes.link} />
          ) : sessionLink.defaultSource === ParticipationSource.PARTNER ? (
            <SharableLink
              link={presentationLink + bilendiParams}
              className={classes.link}
              copyButtonVariant="accent"
              customIcon={classNames(
                'custom-icon-font-bilendi',
                classes.bilendiIcon,
              )}
            />
          ) : (
            <SharableLink
              link={studyLink}
              className={classes.link}
              copyButtonVariant="purple"
              customIcon={classNames(
                'custom-icon-font-tandemz',
                classes.tandemzIcon,
              )}
            />
          )}
        </div>
        <div className={classes.notClickable}>
          <DotsMenu>
            <MenuItem
              label="Modifier la configuration"
              onClick={openSessionLink}
            />
            <MenuItem label="Modifier le Prix" onClick={openPriceEdition} />
            <MenuItem
              label="Modifier la contrepartie"
              onClick={openIncentiveEdition}
            />
            <MenuItem
              label="Editer les liens de redirections"
              onClick={openRedirectionLinks}
            />
            <MenuItem label="Marquer comme par défaut" onClick={setAsDefault} />
            <MenuItem label="Voir tous les liens" onClick={openAllLinks} />
          </DotsMenu>
        </div>
      </div>
      <div
        className={classNames(classes.editionContent, {
          [classes.editionContentClosed]: !openedEdition,
        })}
      >
        {openedEdition === EditionTab.REDIRECTION_LINK && (
          <EditRedirectionLinks
            sessionLinkId={sessionLink.id}
            session={session}
            onSuccess={closeEdition}
          />
        )}
        {openedEdition === EditionTab.PRICE && (
          <PriceEdition sessionLinkId={sessionLink.id} session={session} />
        )}
        {openedEdition === EditionTab.INCENTIVE && (
          <SessionLinkIncentiveEdition
            sessionLink={sessionLink}
            session={session}
            onSuccess={closeEdition}
          />
        )}
        {openedEdition === EditionTab.SESSION_LINK && (
          <EditSessionLink sessionLink={sessionLink} onSuccess={closeEdition} />
        )}
        {openedEdition === EditionTab.ALL_LINKS && (
          <div className={classes.twoCol}>
            <div className={classes.col}>
              <SharableLink
                link={studyLink}
                label="Lien standard"
                copyButtonVariant="purple"
                customIcon={classNames(
                  'custom-icon-font-tandemz',
                  classes.tandemzIcon,
                )}
              />
            </div>
            <div className={classes.col}>
              <SharableLink
                link={presentationLink}
                label="Lien avec présentation"
              />
            </div>
            <div className={classes.col}>
              <SharableLink
                link={studyLink + bilendiParams}
                label="Lien standard pour Bilendi"
                copyButtonVariant="accent"
                customIcon={classNames(
                  'custom-icon-font-bilendi',
                  classes.bilendiIcon,
                )}
              />
            </div>
            <div className={classes.col}>
              <SharableLink
                link={presentationLink + bilendiParams}
                label="Lien avec présentation pour Bilendi"
                copyButtonVariant="accent"
                customIcon={classNames(
                  'custom-icon-font-bilendi',
                  classes.bilendiIcon,
                )}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
